import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import App from './App.tsx'
import { queryClient } from './lib/queryClient.ts'
import { isLocale } from './i18n'
import './index.css'
import './styles/calendar.css'

// Locale prefix (/fr, /th, …) → router basename, so every existing route and
// <Link> works unchanged (React Router prepends/strips it). EN is unprefixed.
const localeSeg = window.location.pathname.split('/')[1]
const routerBasename =
  isLocale(localeSeg) && localeSeg !== 'en' ? `/${localeSeg}` : '/'

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter basename={routerBasename}>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </HelmetProvider>
  </React.StrictMode>
)

// PWA auto-update: the service worker uses skipWaiting + clientsClaim (registerType
// 'autoUpdate'), but the auto-injected registerSW.js never reloads the page — so a
// new deploy's SW would activate while the running app kept serving the OLD
// precached chunks until a manual hard-refresh (the "I deployed but users see old
// code" problem). Reload once when a NEW worker takes control, and proactively
// check for an update when the tab regains focus, so deploys reach users.
if ('serviceWorker' in navigator) {
  const hadController = !!navigator.serviceWorker.controller
  let refreshing = false
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    // Skip the first-ever SW taking control (a new visitor is already on fresh
    // code) and guard against reload loops.
    if (refreshing || !hadController) return
    refreshing = true
    window.location.reload()
  })
  const checkForUpdate = () => {
    navigator.serviceWorker
      .getRegistration()
      .then((reg) => reg?.update())
      .catch(() => {})
  }
  window.addEventListener('focus', checkForUpdate)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkForUpdate()
  })
}
